// ============================================================
// ReprogramarCitaDialog.tsx
// Permite al cliente cambiar la fecha/hora de una cita
// (mismo servicio y profesional). Margen mínimo de 24h.
// ============================================================

import { useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Clock, X } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  DLG_BTN_PRIMARY,
  DLG_BTN_SECONDARY,
} from '@/components/ui/dialogButtons';

import {
  useObtenerHorariosPorRango,
  HorarioItem,
} from '@/components/hooks/useObtenerHorarios';
import { moverCita } from '@/components/hooks/useCitas';

const getHoyISO = () => {
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const a = hoy.getFullYear();
  const m = String(hoy.getMonth() + 1).padStart(2, '0');
  const d = String(hoy.getDate()).padStart(2, '0');
  return `${a}-${m}-${d}`;
};

const generarProximosDias = (cantidad: number, hoyISO: string) => {
  const hoy = new Date(hoyISO + 'T00:00:00');
  return Array.from({ length: cantidad }, (_, i) => {
    const fecha = new Date(hoy);
    fecha.setDate(hoy.getDate() + i);
    const fechaISO = [
      fecha.getFullYear(),
      String(fecha.getMonth() + 1).padStart(2, '0'),
      String(fecha.getDate()).padStart(2, '0'),
    ].join('-');
    return {
      fechaISO,
      diaSemana: format(fecha, 'EEE', { locale: es }),
      diaNumero: format(fecha, 'd', { locale: es }),
    };
  });
};

const calcularHoraFin = (horaInicio: string, duracion: number): string => {
  const [h, m] = horaInicio.split(':').map(Number);
  const inicio = new Date();
  inicio.setHours(h, m, 0, 0);
  const fin = new Date(inicio.getTime() + duracion * 60000);
  return fin.toTimeString().substring(0, 5);
};

// Duración real de la cita (hora_fin - hora_cita); si no es válida, la del
// servicio. Así un servicio por horas conserva las horas reservadas.
const duracionReal = (
  horaCita: string | null | undefined,
  horaFin: string | null | undefined,
  duracionServicio: number
): number => {
  if (!horaCita || !horaFin) return duracionServicio;
  const [h1, m1] = horaCita.split(':').map(Number);
  const [h2, m2] = horaFin.split(':').map(Number);
  const diff = h2 * 60 + m2 - (h1 * 60 + m1);
  if (Number.isNaN(diff) || diff === 0) return duracionServicio;
  return diff > 0 ? diff : diff + 1440;
};

interface ReprogramarCitaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cita: {
    id_cita: string;
    id_servicio: string;
    duracion_minutos: number;
    hora_cita?: string | null;
    hora_fin?: string | null;
    id_trabajador: string | null;
    nombre_servicio: string | null;
  };
  diasVisibles?: number;
  onReprogramada: () => void;
}

const MARGEN_HORAS_MINIMO = 24;

export default function ReprogramarCitaDialog({
  open,
  onOpenChange,
  cita,
  diasVisibles = 14,
  onReprogramada,
}: ReprogramarCitaDialogProps) {
  const hoyISO = useMemo(() => getHoyISO(), []);
  const diasDisponibles = useMemo(
    () => generarProximosDias(diasVisibles, hoyISO),
    [diasVisibles, hoyISO]
  );

  const [fecha, setFecha] = useState(hoyISO);
  const [hora, setHora] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [mostrarTodasHoras, setMostrarTodasHoras] = useState(false);

  const rangoFin = diasDisponibles[diasDisponibles.length - 1]?.fechaISO ?? '';
  const duracion = duracionReal(
    cita.hora_cita,
    cita.hora_fin,
    cita.duracion_minutos
  );

  const { horariosPorDia } = useObtenerHorariosPorRango(
    cita.id_trabajador,
    hoyISO,
    rangoFin,
    cita.id_servicio,
    duracion
  );

  const horasDisponibles: HorarioItem[] = horariosPorDia[fecha] ?? [];

  const cumpleMargen = (fechaISO: string, horaSel: string) => {
    const objetivo = new Date(`${fechaISO}T${horaSel}`);
    const limite = new Date();
    limite.setHours(limite.getHours() + MARGEN_HORAS_MINIMO);
    return objetivo >= limite;
  };

  const handleGuardar = async () => {
    if (!fecha || !hora) {
      toast.error('Selecciona una nueva fecha y hora');
      return;
    }
    if (!cumpleMargen(fecha, hora)) {
      toast.error(
        `Solo puedes reprogramar con al menos ${MARGEN_HORAS_MINIMO}h de antelación`
      );
      return;
    }

    setGuardando(true);
    const horaFin = calcularHoraFin(hora, duracion);
    const resultado = await moverCita(
      cita.id_cita,
      fecha,
      hora,
      horaFin,
      'cliente'
    );
    setGuardando(false);

    if (!resultado.ok) {
      toast.error('No se pudo reprogramar la cita');
      return;
    }

    toast.success('Cita reprogramada con éxito');
    onReprogramada();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="
          z-[60] p-0 border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e]
          [&>button:last-child]:hidden
          flex flex-col
          !bottom-0 !left-0 !top-auto !translate-x-0 !translate-y-0
          w-full max-w-none max-h-[90vh] rounded-t-2xl rounded-b-none
          data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom
          sm:!bottom-auto sm:!left-1/2 sm:!top-1/2 sm:!-translate-x-1/2 sm:!-translate-y-1/2
          sm:w-[95%] sm:max-w-lg sm:mx-auto sm:h-auto sm:max-h-[90vh] sm:rounded-xl sm:shadow-xl
        "
      >
        <div className="px-4 sm:px-6 pt-5 pb-3 shrink-0 relative">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-semibold text-center text-[#103254] dark:text-blue-400 pr-10">
              Cambiar fecha y hora
            </DialogTitle>
            {cita.nombre_servicio && (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                {cita.nombre_servicio}
              </p>
            )}
          </DialogHeader>
          <button
            onClick={() => onOpenChange(false)}
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto overflow-x-hidden px-4 sm:px-6 pb-5">
          <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-sm p-4 space-y-5 overflow-hidden">
            {/* ── Fechas ── */}
            <div className="min-w-0">
              <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[#103254] dark:text-blue-400 mb-2">
                Fecha
              </h3>
              <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 w-full min-w-0">
                {diasDisponibles.map((d) => {
                  const seleccionado = fecha === d.fechaISO;
                  const total = horariosPorDia[d.fechaISO]?.length || 0;
                  return (
                    <button
                      key={d.fechaISO}
                      onClick={() => {
                        if (total > 0) {
                          setFecha(d.fechaISO);
                          setHora('');
                        }
                      }}
                      disabled={total === 0}
                      className={`flex flex-col items-center justify-center h-[68px] min-w-[50px] shrink-0 rounded-xl border transition-all ${
                        seleccionado
                          ? 'bg-[#103254] border-[#103254] text-white shadow-md'
                          : total > 0
                          ? 'bg-white dark:bg-white/5 border-gray-200 dark:border-white/10 text-gray-700 dark:text-gray-300 hover:border-[#103254]/60'
                          : 'bg-gray-50 dark:bg-white/[0.03] border-gray-200 dark:border-white/10 text-gray-300 dark:text-gray-600 cursor-not-allowed'
                      }`}
                    >
                      <span className="text-[10px] font-semibold capitalize opacity-70">
                        {d.diaSemana}
                      </span>
                      <span className="text-lg font-bold leading-none mt-0.5">
                        {d.diaNumero}
                      </span>
                      {total > 0 && (
                        <div
                          className={`w-1 h-1 rounded-full mt-1 ${
                            seleccionado
                              ? 'bg-white'
                              : total <= 4
                              ? 'bg-orange-400'
                              : 'bg-green-400'
                          }`}
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ── Horas ── */}
            <div>
              <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[#103254] dark:text-blue-400 mb-2">
                Hora
              </h3>
              {horasDisponibles.length > 0 ? (
                <>
                  <div className="grid grid-cols-3 gap-2">
                    {horasDisponibles
                      .slice(0, mostrarTodasHoras ? undefined : 9)
                      .map((h) => {
                        const horaFormateada = h.hora.slice(0, 5);
                        const seleccionada = hora === h.hora;
                        return (
                          <button
                            key={h.hora}
                            type="button"
                            onClick={() => setHora(h.hora)}
                            className={`h-11 rounded-xl border text-sm font-semibold transition-all ${
                              seleccionada
                                ? 'bg-[#103254] text-white border-[#103254] shadow-md'
                                : 'bg-white dark:bg-white/5 border-gray-200 dark:border-white/10 text-gray-700 dark:text-gray-300 hover:border-[#103254]/60'
                            }`}
                          >
                            {horaFormateada}
                          </button>
                        );
                      })}
                  </div>
                  {horasDisponibles.length > 9 && (
                    <button
                      onClick={() => setMostrarTodasHoras((p) => !p)}
                      className="w-full mt-3 py-2 text-xs font-semibold text-[#103254] dark:text-blue-400 hover:underline"
                    >
                      {mostrarTodasHoras ? 'Ver menos' : 'Ver más horas'}
                    </button>
                  )}
                </>
              ) : (
                <p className="text-sm text-gray-400 dark:text-gray-500 py-2 flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5" />
                  No hay horarios disponibles para este día.
                </p>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="px-4 sm:px-6 pt-2 pb-5 sm:pb-4 flex flex-col sm:flex-row gap-3 shrink-0 border-t border-gray-100 dark:border-white/10">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={guardando}
            className={`w-full sm:flex-1 ${DLG_BTN_SECONDARY}`}
          >
            Cancelar
          </Button>

          <Button
            type="button"
            onClick={handleGuardar}
            disabled={guardando || !hora}
            className={`w-full sm:flex-1 ${DLG_BTN_PRIMARY}`}
          >
            {guardando ? 'Guardando…' : 'Confirmar cambio'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}